import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Building2, Loader2, X, Command, Clock, User2, ArrowRight, TrendingUp, Flame } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HISTORY_KEY = 'expo_search_history';

const getHistory = () => {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); }
  catch { return []; }
};
const addToHistory = (q) => {
  const prev = getHistory();
  const next = [q, ...prev.filter(x => x !== q)].slice(0, 5);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
};

const MOCK_RESULTS = [
  { type: 'brief', id: 'br_1', title: 'Cityscape Dubai 2025', subtitle: 'Dubai · 95,000 USD', url: '/app/exhibitor/briefs' },
  { type: 'brief', id: 'br_2', title: 'Hannover Messe — Industry 4.0', subtitle: 'Hannover · 160,000 EUR', url: '/app/exhibitor/briefs' },
  { type: 'org',   id: 'org_1', title: 'Istanbul Fair Solutions', subtitle: 'Contractor · Istanbul TR', url: '/app/contractor/dashboard' },
  { type: 'org',   id: 'org_2', title: 'Global Expo Group', subtitle: 'Contractor · New York US', url: '/app/contractor/dashboard' },
];

const TYPE_CONFIG = {
  brief: { icon: FileText,   label: 'Brief',    bg: 'bg-blue-50',   text: 'text-blue-600' },
  org:   { icon: Building2,  label: 'Org',      bg: 'bg-green-50',  text: 'text-green-600' },
  user:  { icon: User2,      label: 'Kullanıcı', bg: 'bg-purple-50', text: 'text-purple-600' },
};

const GlobalSearch = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const [history, setHistory] = useState([]);
  const [trending, setTrending] = useState([]);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const handleKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Focus + load history + fetch trending when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setResults([]);
      setSelected(0);
      setHistory(getHistory());
      // Fetch trending searches
      if (token) {
        axios.get(`${API}/search/trending`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          setTrending(res.data?.trending || []);
        }).catch(() => {});
      }
    }
  }, [open, token]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) { setResults([]); return; }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        if (token) {
          const res = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const items = res.data?.results || [];
          setResults(items.length > 0 ? items : MOCK_RESULTS.filter(r =>
            r.title.toLowerCase().includes(query.toLowerCase()) ||
            r.subtitle.toLowerCase().includes(query.toLowerCase())
          ));
        } else {
          setResults(MOCK_RESULTS.filter(r =>
            r.title.toLowerCase().includes(query.toLowerCase()) ||
            r.subtitle.toLowerCase().includes(query.toLowerCase())
          ));
        }
      } catch {
        setResults(MOCK_RESULTS.filter(r =>
          r.title.toLowerCase().includes(query.toLowerCase())
        ));
      } finally { setLoading(false); }
    }, 280);
  }, [query, token]);

  // Keyboard navigation
  const allItems = query.length >= 2 ? results : [];
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, allItems.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); }
    if (e.key === 'Enter' && allItems[selected]) { handleSelect(allItems[selected]); }
  };

  const handleSelect = (item) => {
    addToHistory(item.title);
    setHistory(getHistory());
    setOpen(false);
    navigate(item.url || '/');
  };

  const handleHistoryClick = (q) => {
    setQuery(q);
    setSelected(0);
    inputRef.current?.focus();
  };

  const handleTrendingClick = (term) => {
    setQuery(term);
    setSelected(0);
    inputRef.current?.focus();
  };

  const clearHistory = (e) => {
    e.stopPropagation();
    localStorage.removeItem(HISTORY_KEY);
    setHistory([]);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-start justify-center pt-[12vh]"
      data-testid="global-search-overlay"
      onClick={e => e.target === e.currentTarget && setOpen(false)}
    >
      <div className="w-full max-w-xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200 animate-in fade-in slide-in-from-top-4 duration-200">

        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-100">
          <Search className="h-5 w-5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setSelected(0); }}
            onKeyDown={handleKeyDown}
            placeholder="Brief, organizasyon, kullanıcı veya şehir ara..."
            data-testid="global-search-input"
            className="flex-1 outline-none text-sm text-gray-800 placeholder-gray-400 bg-transparent"
          />
          {loading && <Loader2 className="h-4 w-4 text-gray-400 animate-spin flex-shrink-0" />}
          {!loading && query && (
            <button onClick={() => { setQuery(''); setResults([]); }} className="text-gray-300 hover:text-gray-500">
              <X className="h-4 w-4" />
            </button>
          )}
          <kbd className="hidden sm:flex items-center px-1.5 py-0.5 text-[10px] text-gray-400 bg-gray-100 border border-gray-200 rounded">esc</kbd>
        </div>

        {/* Results */}
        {allItems.length > 0 && (
          <div className="py-1.5 max-h-72 overflow-y-auto" data-testid="search-results-list">
            {allItems.map((item, idx) => {
              const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.brief;
              const Icon = cfg.icon;
              return (
                <button
                  key={`${item.type}-${item.id}`}
                  onClick={() => handleSelect(item)}
                  data-testid={`search-result-${item.type}-${item.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left ${selected === idx ? 'bg-blue-50' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`h-4 w-4 ${cfg.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{item.title}</p>
                    <p className="text-xs text-gray-400 truncate">{item.subtitle}</p>
                  </div>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0 ${cfg.bg} ${cfg.text}`}>
                    {cfg.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {query.length >= 2 && !loading && allItems.length === 0 && (
          <div className="py-10 text-center text-gray-400">
            <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">"{query}" için sonuç bulunamadı</p>
          </div>
        )}

        {/* Recent searches (shown when input empty) */}
        {query.length === 0 && history.length > 0 && (
          <div className="py-2">
            <div className="flex items-center justify-between px-4 py-1.5">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Son Aramalar</p>
              <button onClick={clearHistory} className="text-[11px] text-gray-400 hover:text-red-500 transition-colors">
                Temizle
              </button>
            </div>
            {history.map((h, idx) => (
              <button
                key={h}
                onClick={() => handleHistoryClick(h)}
                data-testid={`search-history-${idx}`}
                className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 transition-colors text-left"
              >
                <Clock className="h-4 w-4 text-gray-300 flex-shrink-0" />
                <span className="text-sm text-gray-600 flex-1">{h}</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
              </button>
            ))}
          </div>
        )}

        {/* Trending searches (shown when input empty) */}
        {query.length === 0 && trending.length > 0 && (
          <div className="py-2 border-t border-gray-50">
            <div className="flex items-center gap-1.5 px-4 py-1.5">
              <TrendingUp className="h-3.5 w-3.5 text-orange-400" />
              <p className="text-[11px] font-semibold text-orange-400 uppercase tracking-wide">Trend Aramalar</p>
            </div>
            <div className="px-4 py-1.5 flex flex-wrap gap-2" data-testid="trending-searches-list">
              {trending.map((item, idx) => (
                <button
                  key={item.term}
                  onClick={() => handleTrendingClick(item.term)}
                  data-testid={`trending-term-${idx}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 text-xs font-medium rounded-full transition-colors border border-orange-100"
                >
                  <Flame className="h-3 w-3 text-orange-400 flex-shrink-0" />
                  {item.term}
                  {item.count > 1 && (
                    <span className="ml-0.5 text-[10px] text-orange-400 font-normal">×{item.count}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Empty state when no history and no trending */}
        {query.length === 0 && history.length === 0 && trending.length === 0 && (
          <div className="py-8 text-center text-gray-400">
            <Search className="h-7 w-7 mx-auto mb-2 opacity-20" />
            <p className="text-xs">Arama yapmaya başlayın</p>
          </div>
        )}

        {/* Footer */}
        <div className="px-4 py-2.5 text-xs text-gray-400 flex items-center gap-4 border-t border-gray-50">
          <span className="flex items-center gap-1">
            <kbd className="bg-gray-100 border border-gray-200 rounded px-1 py-0.5">↑↓</kbd>Gezin
          </span>
          <span className="flex items-center gap-1">
            <kbd className="bg-gray-100 border border-gray-200 rounded px-1 py-0.5">↵</kbd>Aç
          </span>
          <span className="flex items-center gap-1">
            <kbd className="bg-gray-100 border border-gray-200 rounded px-1 py-0.5">esc</kbd>Kapat
          </span>
          <span className="ml-auto flex items-center gap-1">
            <Command className="h-3 w-3" />K ile her yerden aç
          </span>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
